import { useState, useEffect, useCallback } from 'react';
import { msalInstance, loginRequest } from '../lib/msal';
import { getTenantSlug } from '../lib/tenantMode';

export interface AuthUser {
  id:       number | null;
  username: string;
  name:     string;
  role:     string;
}

const API_URL = process.env.REACT_APP_API_URL ?? 'http://localhost:8000';

/** Call /auth/me with the HttpOnly cookie — returns the profile or null. */
async function fetchMe(): Promise<AuthUser | null> {
  try {
    const res = await fetch(`${API_URL}/auth/me`, {
      credentials: 'include',
    });
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
}

/** Tell the backend to expire the cookie. */
async function apiLogout(): Promise<void> {
  try {
    await fetch(`${API_URL}/auth/logout`, {
      method:      'POST',
      credentials: 'include',
    });
  } catch {
    // best-effort
  }
}

export function useAuth() {
  const [user,    setUser]    = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [msError, setMsError] = useState<string | null>(null);

  // ── Listen for 401 events dispatched by client.ts ─────────────────────────
  useEffect(() => {
    const onLogout = () => setUser(null);
    window.addEventListener('gws:logout', onLogout);
    return () => window.removeEventListener('gws:logout', onLogout);
  }, []);

  // ── Restore session on mount (cookie is sent automatically) ───────────────
  useEffect(() => {
    // Cookie check is the primary auth path — always clears loading immediately.
    fetchMe().then(me => {
      setUser(me);
      setLoading(false);

      // MSAL runs in the background only to handle Microsoft SSO redirects.
      // It never blocks the loading state.
      if (me) return;  // already authenticated — skip MSAL entirely
      msalInstance.initialize().then(() => {
        msalInstance.handleRedirectPromise().then(async (result) => {
          if (!result?.idToken) return;
          try {
            const res = await fetch(`${API_URL}/auth/microsoft`, {
              method:      'POST',
              headers:     { 'Content-Type': 'application/json' },
              body:        JSON.stringify({ id_token: result.idToken }),
              credentials: 'include',
            });
            if (res.ok) {
              const data = await res.json();
              setUser(data.user ?? null);
            } else {
              const err = await res.json().catch(() => ({}));
              setMsError(err.detail ?? `Microsoft login failed (${res.status})`);
            }
          } catch {
            setMsError('Could not reach the server after Microsoft login.');
          }
        }).catch((e: any) => {
          if (e?.errorCode !== 'user_cancelled') {
            setMsError(e?.message ?? 'Microsoft login failed.');
          }
        });
      }).catch(() => {});
    });
  }, []);

  // ── Password login ─────────────────────────────────────────────────────────
  const login = useCallback(async (username: string, password: string): Promise<string | null> => {
    const body = new URLSearchParams({ username, password });
    // When the SPA isn't served from <slug>.app.goeswestservices.com (e.g.,
    // Firebase default sites like gws-<slug>.web.app, or local dev), the
    // backend can't resolve the tenant from the Host header. Append the slug
    // as ?tenant= so the login handler scopes the User lookup correctly.
    // getTenantSlug() reads REACT_APP_TENANT_SLUG first (build-time pin),
    // then falls back to the URL subdomain.
    const slug = getTenantSlug();
    const url  = slug ? `${API_URL}/auth/login?tenant=${encodeURIComponent(slug)}` : `${API_URL}/auth/login`;
    const res  = await fetch(url, {
      method:      'POST',
      headers:     { 'Content-Type': 'application/x-www-form-urlencoded' },
      body:        body.toString(),
      credentials: 'include',   // browser stores the Set-Cookie from the response
    });
    if (!res.ok) return 'Invalid username or password';
    const data = await res.json();
    setUser(data.user ?? null);
    return null;
  }, []);

  // ── Microsoft SSO ──────────────────────────────────────────────────────────
  const loginWithMicrosoft = useCallback(async (): Promise<string | null> => {
    try {
      await msalInstance.initialize();
      await msalInstance.loginRedirect(loginRequest);
      return null;
    } catch (e: any) {
      if (e.errorCode === 'user_cancelled') return null;
      return e.message ?? 'Microsoft login failed';
    }
  }, []);

  // ── Logout ─────────────────────────────────────────────────────────────────
  const logout = useCallback(async () => {
    await apiLogout();   // server expires the cookie
    // Clear per-user chat history so it doesn't survive logout
    if (user?.id) localStorage.removeItem(`luana_chat_${user.id}`);
    setUser(null);
    window.location.href = '/';
  }, [user]);

  // ── Refresh (re-check the cookie) ──────────────────────────────────────────
  const refresh = useCallback(async () => {
    const me = await fetchMe();
    setUser(me);
  }, []);

  // token is intentionally empty — auth is now cookie-based.
  // Pages that still destructure `token` get '' and should be migrated to
  // rely on credentials:'include' in their fetch calls instead.
  return { user, token: '' as string, loading, msError, login, loginWithMicrosoft, logout, refresh };
}
